import React, {
  useState,
  useEffect,
  useCallback,
  createRef,
  Fragment,
} from "react";
import { useDispatch } from "react-redux";

import { Box } from "react-bulma-components";

import dayjs from "dayjs";
import localeData from "dayjs/plugin/localeData";
import objectSupport from "dayjs/plugin/objectSupport";

import { useAffiliationChangeInformation } from "./common";
import { changesSlice } from "redux/features/changes";

import ErrorMessage from "util/errorMessage";
import IconButton from "util/iconButton";
import { FaCheck, FaTimes, FaAngleDown } from "react-icons/fa";

import MonthPicker from "react-month-picker";

dayjs.extend(localeData);
dayjs.extend(objectSupport);

export function ChangeAffiliationStartDate({ id, statusRecord, onFinished }) {
  const [newDate, setNewDate] = useState(
    dayjs(statusRecord.from).startOf("month").format("YYYY-MM-DD")
  );

  const prevRecord = statusRecord.previousRecord;
  const [changeRecord, changeKeys] = useAffiliationChangeInformation(
    id,
    statusRecord.from ?? "infinite-future"
  );

  const [prevChangeRecord, prevChangeKeys] = useAffiliationChangeInformation(
    id,
    prevRecord ? prevRecord.through : "infinite-past"
  );

  const [saving, setSaving] = useState(null);
  const [failed, setFailed] = useState(null);

  const dispatch = useDispatch();

  const doClose = useCallback(() => {
    setSaving(null);
    onFinished();
  }, [setSaving, onFinished]);

  const doSave = () => {
    setFailed(null);
    if (newDate === statusRecord.from) doClose();
    if (newDate < statusRecord.from) {
      dispatch(
        changesSlice.actions.setChanged({
          ...changeKeys,
          field: "from",
          value: newDate,
        })
      );

      dispatch(changesSlice.asyncActions.save(changeKeys));
      setSaving("earlier");
    } else {
      dispatch(
        changesSlice.actions.setChanged({
          ...prevChangeKeys,
          field: "through",
          value: dayjs(newDate).subtract(1, "day").format("YYYY-MM-DD"),
        })
      );
      dispatch(changesSlice.asyncActions.save(prevChangeKeys));
      setSaving("later");
    }
  };

  useEffect(() => {
    if (changeRecord && !saving) doClose();
    if (prevChangeRecord && !saving) doClose();
    if (saving === "earlier" && !changeRecord) doClose();
    if (saving === "later" && !prevChangeRecord) doClose();
    if (saving === "earlier" && changeRecord && changeRecord.failed) {
      setFailed(changeRecord.failed.message);
      dispatch(changesSlice.actions.deleteChangeRecord(changeKeys));
      setSaving(null);
    }
    if (saving === "later" && prevChangeRecord && prevChangeRecord.failed) {
      setFailed(prevChangeRecord.failed.message);
      dispatch(changesSlice.actions.deleteChangeRecord(prevChangeKeys));
      setSaving(null);
    }
  }, [
    changeRecord,
    saving,
    doClose,
    prevChangeRecord,
    dispatch,
    changeKeys,
    prevChangeKeys,
  ]);

  const earliestYear = dayjs().year() - 100;
  const curFrom = statusRecord.from;
  const today = dayjs().format("YYYY-MM-DD");
  const curThrough = statusRecord.through || today;
  const maxFrom = curThrough > today ? today : curThrough;

  const prevFrom = prevRecord.from || prevRecord.entered_from;
  const pickerRef = createRef(null);

  if (saving) return <Box>Saving changes ...</Box>;
  return (
    <Fragment>
      {failed ? (
        <ErrorMessage header="Unable to Save Changes">{failed}</ErrorMessage>
      ) : null}
      <Box className="has-background-info-light">
        {statusRecord.roundedFrom ? null : (
          <p className="is-italic">
            We prefer to track affiliation status for complete months, and will
            set the start date to the first of the month you selected below. If
            there was a mid-month change, please choose between the month of the
            change or the month after.
          </p>
        )}
        <div>
          Starting month:{" "}
          <IconButton
            icon={FaAngleDown}
            rightIcon
            size="medium"
            onClick={() => {
              if (pickerRef) pickerRef.current.show();
            }}
          >
            {dayjs(newDate).format("MMMM YYYY")}
          </IconButton>
        </div>
        <MonthPicker
          ref={pickerRef}
          years={{
            min: {
              year: earliestYear,
              month: 1,
            },
            max: {
              year: Number(maxFrom.substring(0, 4)),
              month: Number(maxFrom.substring(5, 7)),
            },
          }}
          value={{
            year: Number(newDate.substring(0, 4)),
            month: Number(newDate.substring(5, 7)),
          }}
          lang={dayjs.monthsShort()}
          onChange={(year, month) => {
            setNewDate(
              dayjs({ year, month: month - 1, day: 1 }).format("YYYY-MM-DD")
            );
            setFailed(null);
            pickerRef.current.dismiss();
          }}
        />
        {prevFrom && newDate <= prevFrom ? (
          <p className="affil-date-warning">
            <b>Note:</b> Setting the start month this early will cause the
            complete replacement of the previous information that was in our
            database beginning {dayjs(prevFrom).format("MMMM D, YYYY")}
          </p>
        ) : null}
        <div className="buttons">
          {newDate !== curFrom ? (
            <IconButton icon={FaCheck} onClick={doSave}>
              Save Changes
            </IconButton>
          ) : null}
          <IconButton icon={FaTimes} onClick={doClose}>
            {newDate !== curFrom ? "Cancel Changes" : "Close"}
          </IconButton>
        </div>
      </Box>
    </Fragment>
  );
}

export default ChangeAffiliationStartDate;
